import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Anchor,
  AppButton,
  Box,
  capitalizeFirstLetter,
  List,
  Preloader,
  Text,
  WarningIcon,
  WarnModal,
} from '@common-fe/common-fe';
import styled, { css } from 'styled-components';

import { ROUTES } from '@/common';
import { EMPTY_FILE_FIELD_VALUE } from '@/common/constants';
import Permissions from '@/common/permissions';
import { useHasAccess, useHistory, useSnackbar } from '@/modules/core/hooks';
import theme from '@/styles/theme';

import { SnackbarState } from '../core/core.types';

import ProcessLink from './hooks/ProcessLink';
import { ERROR_TEXT, FormattedDate, getAvatar, ManyNames, UploadedBy } from './hooks/useFilesList';
import { useFileCancelQuery } from './queries/useFileCancel.query';
import useFileRemoveQuery from './queries/useFileRemove.query';
import FileDuplicationProcess from './FileDuplicationProcess';
import { FileDtoWithMediaSource, FileStatus } from './FileManager.constants';
import FileWarning from './FileWarning';
import { useAllowProcessAdjustmentFile } from './hooks';
import { useFilesListFilterStore } from './stores';

interface WrapperProps {
  fileStatus?: FileStatus;
}
const Wrapper = styled(List.Item)<WrapperProps>`
  ${({ fileStatus, theme }) => {
    let boxShadow: object | null = null;
    let borderColor: string | null = null;
    if (fileStatus === FileStatus.Uploading) {
      borderColor = theme.colors.border;
    }
    if (
      fileStatus === FileStatus.Uploaded
      //   || fileStatus === FileStatus.Approved
      // || fileStatus === FileStatus.Validated
    ) {
      boxShadow = theme.shadows.primary;
      borderColor = theme.colors.accentActive;
    }

    if (fileStatus === FileStatus.Error) {
      boxShadow = theme.shadows.error;
      borderColor = theme.colors.danger;
    }
    if (fileStatus === FileStatus.Processing) {
      boxShadow = theme.shadows.warning;
      borderColor = theme.colors.warning;
    }
    if (fileStatus === FileStatus.Completed) {
      boxShadow = theme.shadows.success;
      borderColor = theme.colors.success;
    }
    if (fileStatus === FileStatus.Duplicate) {
      boxShadow = theme.shadows.error;
      borderColor = theme.colors.dangerBorder;
    }
    return css`
      ${boxShadow ? (boxShadow as unknown as string) : ''};
      ${borderColor ? `border: 2px solid ${borderColor};` : null}
      &:hover {
        ${boxShadow ? (boxShadow as unknown as string) : ''};
        ${borderColor
          ? css`
              border: 2px solid ${borderColor};
            `
          : css`
              outline: 2px solid ${theme.colors.border1};
            `}
      }
    `;
  }}
`;

interface Props extends FileDtoWithMediaSource {
  sendingToProcessFileId?: string;
  onReuploading: () => void;
  onProcessFile: () => void;
  onCleanProcessedFile: (id: string) => void;
  onDownload: () => void;
}
const FilesListItem: React.FC<Props> = ({
  type,
  status,
  name,
  id,
  temporalId,
  partnersNames = [],
  hasReuploading,
  employersNames = [],
  uploadedBy,
  uploadWay,
  createdAt,
  processedAt,
  errorsCount,
  backlighting,
  sendingToProcessFileId,
  onReuploading,
  onProcessFile,
  onDownload,
  onCleanProcessedFile,
  isQualityGateError,
}) => {
  const [isConfirmCancelModal, setConfirmCancelModal] = useState(false);
  const [isConfirmRemoveModal, setConfirmRemoveModal] = useState(false);
  const setDuplicationItem = useFilesListFilterStore((state) => state.setDuplicationItem);
  const isAllowedProcessAdjustment = useAllowProcessAdjustmentFile(type);
  const hasAccessToProcessFile = useHasAccess([
    {
      permission: Permissions.PROCESS_FILE,
    },
  ]);
  const isWarningIcon = useMemo(
    () => status === FileStatus.Completed && !!errorsCount,
    [errorsCount, status]
  );
  const hasProcessButton = useMemo(() => {
    return status === FileStatus.Uploaded;
  }, [status]);
  const history = useHistory();
  const handleOpenDetails = useCallback(() => {
    history.push(`${ROUTES.FILE_DETAILS}/${id || temporalId}`);
  }, [history, id, temporalId]);

  const statusColor = useMemo(() => {
    if (status === FileStatus.Uploading) {
      return theme.colors.statusColors.uploading;
    }

    if (status === FileStatus.Processing) {
      return theme.colors.statusColors.processingPending;
    }

    if (status === FileStatus.Error || status === FileStatus.Duplicate || status === FileStatus.Canceled) {
      return theme.colors.statusColors.deniedErrorExpired;
    }

    if (status === FileStatus.Uploaded) {
      return theme.colors.statusColors.uploadedApproved;
    }
    if (status === FileStatus.Completed) {
      return theme.colors.statusColors.paidActiveComplete;
    }

    return theme.colors.statusColors.lockedNull;
  }, [status]);

  const statusTitle = useMemo(() => {
    return capitalizeFirstLetter(status);
  }, [status]);

  const handleOpenDuplicates = useCallback(() => {
    setDuplicationItem({
      key: id,
      value: name,
    });
  }, [id, name, setDuplicationItem]);

  const isDuplication = useMemo(() => {
    return status === FileStatus.Duplicate;
  }, [status]);
  const isCanceled = useMemo(() => {
    return status === FileStatus.Canceled;
  }, [status]);

  const { handleCancel, isLoading: isCancelLoading } = useFileCancelQuery(id);
  const { handleRemove, isLoading: isRemoveLoading } = useFileRemoveQuery(id);
  const [processModalVisible, setProcessModalVisible] = useState(false);
  const handleOpenProcessModal = useCallback(() => {
    setProcessModalVisible(true);
  }, []);
  const { handleAddSnackbar } = useSnackbar();
  const handleReject = useCallback(async () => {
    await handleCancel();
    onCleanProcessedFile(id);
    setConfirmCancelModal(false);
    handleAddSnackbar({
      text: 'File canceled',
      closeIcon: true,
      state: SnackbarState.negative,
    });
  }, [handleAddSnackbar, handleCancel, id, onCleanProcessedFile]);
  const handleDelete = useCallback(async () => {
    await handleRemove();
    onCleanProcessedFile(id);
    setConfirmRemoveModal(false);
    handleAddSnackbar({
      text: 'File removed',
      closeIcon: true,
      state: SnackbarState.negative,
    });
  }, [handleAddSnackbar, handleRemove, id, onCleanProcessedFile]);
  const isFileProcessing = useMemo(() => sendingToProcessFileId === id, [id, sendingToProcessFileId]);
  useEffect(() => {
    if (isFileProcessing) {
      setConfirmRemoveModal(false);
    }
  }, [isFileProcessing]);
  const isRemoveAvailable = useMemo(() => {
    return (
      status === FileStatus.Uploaded ||
      status === FileStatus.Initiated ||
      status === FileStatus.Parsed ||
      status === FileStatus.Validated ||
      status === FileStatus.Canceled ||
      (status === FileStatus.Error && !errorsCount) ||
      status === FileStatus.Duplicate
    );
  }, [errorsCount, status]);
  const isCancelAllowed = useMemo(() => {
    return (
      status === FileStatus.Uploaded ||
      status === FileStatus.Initiated ||
      status === FileStatus.Parsed ||
      status === FileStatus.Validated ||
      (status === FileStatus.Error && !errorsCount) ||
      status === FileStatus.Duplicate
    );
  }, [errorsCount, status]);

  return (
    <Wrapper fileStatus={backlighting}>
      <WarnModal
        testId="cancel-file-modal"
        iconNode={<WarningIcon size="48px" />}
        visible={isConfirmCancelModal}
        header="Cancel File?"
        onSetVisible={setConfirmCancelModal}
        cancelButtonNode={
          <Box direction="row">
            <Box width="150px" margin={{ right: 'spacing6' }}>
              <AppButton buttonType="secondary" onClick={() => setConfirmCancelModal(false)} width="100%">
                Close
              </AppButton>
            </Box>
            <Box width="150px" margin={{ left: 'spacing6' }}>
              <AppButton color="red" onClick={handleReject} width="100%" disabled={isCancelLoading}>
                {isCancelLoading ? <Preloader color="white" /> : 'Yes, Cancel'}
              </AppButton>
            </Box>
          </Box>
        }
      />
      <WarnModal
        testId="remove-file-modal"
        iconNode={<WarningIcon size="48px" />}
        visible={isConfirmRemoveModal}
        header="Remove File?"
        onSetVisible={setConfirmRemoveModal}
        cancelButtonNode={
          <Box direction="row">
            <Box width="150px" margin={{ right: 'spacing6' }}>
              <AppButton buttonType="secondary" onClick={() => setConfirmRemoveModal(false)} width="100%">
                Close
              </AppButton>
            </Box>
            <Box width="150px" margin={{ left: 'spacing6' }}>
              <AppButton color="red" onClick={handleDelete} width="100%" disabled={isRemoveLoading}>
                {isRemoveLoading ? <Preloader color="white" /> : 'Yes, Remove'}
              </AppButton>
            </Box>
          </Box>
        }
      />
      <Box direction="row" align="center">
        <Box pad={{ right: 's' }}>
          <Box
            justify="center"
            align="center"
            width={{ max: '34px', min: '34px' }}
            height={{ min: '34px', max: '34px' }}
            border={{ size: 'small', color: 'border1' }}
            round="dropdownItemRound"
          >
            {getAvatar(type, status, isCanceled)}
          </Box>
        </Box>
      </Box>
      <Box style={{ flex: 1.15 }}>
        <Anchor color="textBody" onClick={handleOpenDetails}>
          <Text weight={400} truncate>
            {name || EMPTY_FILE_FIELD_VALUE}
          </Text>
        </Anchor>
      </Box>
      <Box style={{ flex: 0.8 }} pad={{ right: 's' }}>
        <Text color={isCanceled ? 'textDisabled' : ''} weight={400}>
          {partnersNames?.length ? (
            <ManyNames names={partnersNames} />
          ) : hasReuploading ? (
            ''
          ) : (
            EMPTY_FILE_FIELD_VALUE
          )}
        </Text>
      </Box>

      <Box style={{ flex: 0.9 }} pad={{ right: 's' }}>
        <Text color={isCanceled ? 'textDisabled' : ''} weight={400}>
          {employersNames?.length ? (
            <ManyNames names={employersNames} />
          ) : hasReuploading ? (
            ''
          ) : (
            EMPTY_FILE_FIELD_VALUE
          )}
        </Text>
      </Box>

      <Box style={{ flex: 0.85 }} color={theme.colors.textSecondary} pad={{ right: 's' }}>
        <Text color={isCanceled ? 'textDisabled' : ''} weight={400}>
          {hasReuploading ? (
            ''
          ) : uploadedBy ? (
            <UploadedBy name={uploadedBy} way={uploadWay} />
          ) : (
            EMPTY_FILE_FIELD_VALUE
          )}
        </Text>
      </Box>

      <Box style={{ flex: 1.1 }} pad={{ right: 's' }}>
        {hasReuploading ? (
          <Text color={isCanceled ? 'textDisabled' : 'danger'} weight={400} title={ERROR_TEXT} truncate>
            {ERROR_TEXT}
          </Text>
        ) : (
          <Box>
            <Text color={isCanceled ? 'textDisabled' : ''} weight={400}>
              {createdAt || processedAt ? (
                <FormattedDate from={createdAt} to={processedAt} />
              ) : (
                EMPTY_FILE_FIELD_VALUE
              )}
            </Text>
          </Box>
        )}
      </Box>
      <Box style={{ flex: 0.45 }} pad={{ right: 's' }}>
        <FileWarning
          isWarningIcon={isWarningIcon}
          fileStatus={status}
          onSearchDuplicate={handleOpenDuplicates}
        />
      </Box>

      {hasReuploading ? (
        <Box style={{ flex: 0.8 }}>
          <ProcessLink onFileProcess={onReuploading} testId={id || temporalId} title="Reupload" />
        </Box>
      ) : (
        <Box direction="row-reverse" align="center" style={{ flex: 0.8 }} margin={{ right: '20px' }}>
          <Box align="center" direction="row-reverse">
            <Box
              width={{ min: '11px', max: '11px' }}
              height={{ min: '11px', max: '11px' }}
              round="50%"
              background={statusColor}
            />
            <Text margin={{ right: 's' }} weight={400}>
              {statusTitle}
            </Text>
          </Box>
        </Box>
      )}
      {isDuplication && (
        <FileDuplicationProcess
          fileId={id}
          visible={processModalVisible}
          onCleanProcessedFile={onCleanProcessedFile}
          onSetVisible={setProcessModalVisible}
          onSearchDuplicate={handleOpenDuplicates}
        />
      )}
      <List.Item.Options>
        {isDuplication && !isCanceled && (
          <List.Item.Options.Item onClick={handleOpenProcessModal}>Process</List.Item.Options.Item>
        )}
        {hasAccessToProcessFile && hasProcessButton && isAllowedProcessAdjustment && !isCanceled && (
          <List.Item.Options.Item onClick={onProcessFile}>Process</List.Item.Options.Item>
        )}

        <List.Item.Options.Item onClick={handleOpenDetails}>File summary</List.Item.Options.Item>
        {isDuplication || isCanceled ? null : (
          <List.Item.Options.Item onClick={onDownload}>Download</List.Item.Options.Item>
        )}

        {((hasAccessToProcessFile && isCancelAllowed) || isDuplication || isFileProcessing) &&
          !isCancelLoading &&
          !isQualityGateError &&
          !isRemoveLoading && (
            <List.Item.Options.Item onClick={() => setConfirmCancelModal(true)}>
              Cancel
            </List.Item.Options.Item>
          )}
        {hasAccessToProcessFile &&
          !isQualityGateError &&
          isRemoveAvailable &&
          !isFileProcessing &&
          !isCancelLoading &&
          !isRemoveLoading && (
            <List.Item.Options.Item onClick={() => setConfirmRemoveModal(true)}>
              Remove
            </List.Item.Options.Item>
          )}
      </List.Item.Options>
    </Wrapper>
  );
};

export default FilesListItem;
